import React, { useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import clsx from 'clsx';
import {
  AccordionSummary,
  Button,
  Checkbox,
  CheckboxProps,
  Grid,
  Tooltip,
  Typography,
  Link,
  FormControlLabel,
  FormControl,
  Tabs as MuiTabs,
  Tab as MuiTab,
  Accordion as MuiAccordion,
  Theme,
  styled,
} from '@quillbot/design-system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { renderToString } from 'react-dom/server';
import { CitationData, CitedData } from 'src/citation-generator/types';
import researcherTrack from 'src/researcher/handlers/track';
import { getShortUrl } from 'core/utils';
import { CitationElement } from 'src/citation-generator/components/CitationElement';
import { handleCitationNumberOnText } from 'src/citation-generator/utils';
import { CitationSpanClassName, LAYOUT_VALS } from 'src/researcher/constants/common.constants';
import { decodeHTMLEntities } from 'src/researcher/utils';
import { ClosingQuoteIcon, OpeningQuoteIcon } from '@Common/quillElements/icon';
import { nonSelectable } from 'src/researcher/styles';
import { WebsiteLinkHoverTooltip } from 'src/researcher/constants/message.constants';

export { Tooltip } from './Tooltip';
export { TextDialogWithCircularProgress } from './Dialog';
export { TabLayout, TabHeader, TabBody, TabFooter } from './TabLayout';
export { AsyncIconButton, AsyncButton } from './Button';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
  },
  icon: {
    borderRadius: '50%',
    margin: '3px 0px 3px 12px',
    width: 16,
    height: 16,
    padding: 0,
    border: '2px solid rgba(0, 0, 0, 0.54)',
  },
  disabledIcon: {
    borderRadius: 2,
    width: 16,
    margin: '3px 0px 3px 12px',
    height: 16,
    padding: 0,
    border: '2px solid #D0D0D0',
  },

  checkedIcon: {
    border: 'none',
    margin: '3px 0px 3px 12px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    padding: 0,
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
    '&:before': {
      display: 'block',
      width: 16,
      height: 16,
      backgroundImage: 'url("/images/coWriter/checkedIcon.svg")',
      content: '""',
    },
  },
  labelRoot: {
    marginRight: '8px',
    marginLeft: '10px',
    paddingBottom: '1px',
    padding: 0,
    maxHeight: '25px',
    '& span': {
      padding: 0,
    },
  },
  controlLable: {
    textAlign: 'center',
  },
}));

interface IProps extends CheckboxProps {
  label: string;
}

const useStyles1 = makeStyles({
  root: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  icon: {
    margin: '3px 0px 3px 6px',
    borderRadius: '50%',
    width: 16,
    height: 16,
    boxShadow: 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
    backgroundColor: '#f5f8fa',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
    '$root.Mui-focusVisible &': {
      outline: '2px auto rgba(19,124,189,.6)',
      outlineOffset: 2,
    },
    'input:disabled ~ &': {
      boxShadow: 'none',
      background: 'rgba(206,217,224,.5)',
    },
  },
  checkedIcon: {
    margin: '3px 0px 3px 6px',
    backgroundColor: '#fff',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
    '&:before': {
      display: 'block',
      width: 16,
      height: 16,
      backgroundImage: 'url("/images/coWriter/checkedIcon.svg")',
      content: '""',
    },
  },
});

// Inspired by blueprintjs
function StyledCheckbox(props: CheckboxProps) {
  const classes = useStyles1();
  const { checked } = props;

  return (
    <Checkbox
      className={classes.root}
      disableRipple
      color="default"
      checkedIcon={
        checked ? (
          <span className={clsx(classes.icon, classes.checkedIcon)} />
        ) : (
          <span className={classes.icon} />
        )
      }
      icon={<span className={classes.icon} />}
      {...props}
    />
  );
}
export function TopicCheckBox(props: IProps) {
  const { label, checked } = props;
  const classes = useStyles();
  return (
    <FormControl
      variant="outlined"
      style={{
        backgroundColor: checked ? '#219653' : 'rgb(241,241,241)',
        borderRadius: '5px',
        maxHeight: '25px',
      }}
    >
      <FormControlLabel
        control={<StyledCheckbox {...props} />}
        classes={{
          root: classes.labelRoot,
          label: classes.controlLable,
        }}
        labelPlacement="start"
        label={
          <span
            style={{
              padding: 0,
              fontSize: '14px',
              color: checked ? '#fff' : undefined,
            }}
          >
            {label}
          </span>
        }
      />
    </FormControl>
  );
}

export const Accordion = withStyles({
  root: {
    border: '1px solid rgba(0, 0, 0, .125)',
    boxShadow: 'none',
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
    },
  },
  expanded: {},
})(MuiAccordion);

const useStyles3 = makeStyles(() => ({
  summary: {
    margin: '6px 0px !important',
    minHeight: '10px',
    paddingTop: '3px',
    paddingBottom: '3px',
    paddingLeft: '4px',
  },
  expandIcon: {
    padding: 0,
  },
  expanded: {
    minHeight: '20px !important',
  },
  accordianRoot: {
    minHeight: '30px',
    '&:hover': {
      backgroundColor: 'rgba(76, 148, 89, 0.13)',
    },
  },
  accordianText: {
    cursor: 'pointer',
    fontSize: '14px',
    lineHeight: '21px',
    color: '#252525',
    '&:hover': {
      color: '#000000',
    },
  },
}));

const useStaticsStyles = makeStyles((theme: Theme) => ({
  summary: {
    margin: '8px 0px 10px !important',
    border: 'none',
  },
  root: {
    border: '1px solid #DEE1E3',
    boxShadow: '0px 3px 5px rgb(77 77 77 / 10%)',
    width: '100%',
  },
  expandIcon: {
    padding: 5,
    border: 'none',
  },
  expanded: {
    border: `1px solid ${theme.palette.primary.main}`,
    marginBottom: '10px',
    boxShadow: 'none',
    width: '100%',
  },
  accordianRoot: {
    minHeight: '30px',
  },
  accordianText: {
    cursor: 'pointer',
    fontSize: '14px',
    lineHeight: '21px',
    color: '#252525',
    '&:hover': {
      color: '#000000',
    },
  },
  link: {
    fontWeight: 600,
    fontSize: '14px',
    marginTop: '6px',
    color: theme.palette.text.primary,
    '&:hover': {
      color: theme.specialColors.BLUE_COLOR,
    },
  },
}));

const useStyles2 = makeStyles(() => ({
  tooltip: {
    padding: 0,
  },
  tooltipPlacementBottom: {
    minHeight: '20px !important',
  },
}));

export const WriterAccordion = (props: {
  style?: React.CSSProperties;
  children: JSX.Element | JSX.Element[];
  heading: string;
  expanded?: boolean;
  url?: string;
}) => {
  const classes = useStyles3();
  const { children, heading, expanded } = props;

  useEffect(() => {
    if (expanded && props.url) {
      researcherTrack.userOpenedStatsAccordion(props.url);
    }
  }, [expanded, props.url]);

  return (
    <Accordion
      expanded={expanded}
      onChange={(e, exp) => {
        const selection = document.getSelection();
        if (selection && selection.rangeCount) {
          selection.collapseToStart();
        }
        if (exp) {
          researcherTrack.userInteractedWithHighlight();
        }
      }}
      style={{
        backgroundColor: '#F5F5F5',
        width: '100%',
        borderRadius: '5px',
        maxWidth: '100%',
        marginBottom: '10px',
        ...props.style,
      }}
    >
      <AccordionSummary
        classes={{
          content: classes.summary,
          expandIconWrapper: classes.expandIcon,
          expanded: classes.expanded,
          root: classes.accordianRoot,
        }}
        expandIcon={<ExpandMoreIcon />}
        aria-label="Expand"
        aria-controls="additional-actions1-content"
        id="additional-actions1-header"
      >
        {decodeHTMLEntities(heading)}
      </AccordionSummary>
      <div
        style={{
          backgroundColor: '#fafafa',
          paddingTop: '10px',
          wordBreak: 'break-word',
        }}
      >
        {children}
      </div>
    </Accordion>
  );
};

export const StaticsAccordion = (props: {
  children: JSX.Element | JSX.Element[];
  heading: string;
  expanded?: boolean;
  url: string;
  'data-citation-info'?: string;
}) => {
  const classes = useStaticsStyles();
  const { children, heading, expanded, url } = props;
  return (
    <Accordion
      expanded={expanded}
      classes={{
        expanded: classes.expanded,
        root: classes.root,
      }}
      onChange={(e, exp) => {
        const selection = document.getSelection();
        if (selection && selection.rangeCount) {
          selection.collapseToStart();
        }
      }}
      style={{ margin: '0px 0px 12px' }}
    >
      <AccordionSummary
        classes={{
          content: classes.summary,
          expandIconWrapper: classes.expandIcon,
          root: classes.accordianRoot,
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        <Grid container>
          <Grid container item>
            <Tooltip title={WebsiteLinkHoverTooltip} arrow>
              <Typography
                sx={nonSelectable}
                style={{
                  color: '#252525',
                  fontSize: '13px',
                }}
              >
                {getShortUrl(url)}
              </Typography>
            </Tooltip>
          </Grid>
          <Grid container item>
            <Link target="_blank" href={url}>
              <Typography
                sx={nonSelectable}
                className={clsx(classes.link)}
                style={{
                  fontWeight: 600,
                  fontSize: '14px',
                  marginTop: '6px',
                  color: '#252525',
                }}
              >
                {decodeHTMLEntities(heading)}
              </Typography>
            </Link>
          </Grid>
        </Grid>
      </AccordionSummary>
      <div
        style={{
          padding: '17px 4px 17px 0px',
          wordBreak: 'break-word',
          borderTop: '1px solid #DEE1E3',
        }}
      >
        <Grid
          data-citation-info={props['data-citation-info']}
          container
          onScroll={(e) => e.stopPropagation()}
          style={{
            maxHeight: '160px',
            overflow: 'auto',
            paddingRight: '33px',
          }}
        >
          {children}
        </Grid>
      </div>
    </Accordion>
  );
};
interface InsertButtonToolTipProps {
  onClick: Function;
  children: JSX.Element;
}

export const createCitationTooltipHtml = (metaInfo: CitationData, citedData: CitedData) => {
  const tooltipText = handleCitationNumberOnText(
    citedData.full.map((c) => c.text).join(''),
    citedData.citationNumber
  );

  const inTextCitationHTMLComponent = renderToString(
    <Tooltip title={tooltipText} arrow placement="right">
      <Grid component="span" className="mceNonEditable" style={{ cursor: 'pointer' }}>
        <span
          className={`${CitationSpanClassName} CitationMarkerStyle--allStyles`}
          data-ref={metaInfo.refId || metaInfo.id}
        >
          <CitationElement content={citedData.short} citationNumber={citedData.citationNumber} />
        </span>
      </Grid>
    </Tooltip>
  );

  return inTextCitationHTMLComponent;
};

export const InsertButtonToolTip = (props: InsertButtonToolTipProps) => {
  const classes = useStyles2();
  const onClick = () => {
    props.onClick();
  };
  return (
    <Tooltip
      placement="bottom-end"
      classes={{
        tooltip: classes.tooltip,
        tooltipPlacementBottom: classes.tooltipPlacementBottom,
      }}
      title={
        <Button
          variant="contained"
          color="primary"
          onClick={onClick}
          style={{
            borderRadius: '100px',
            fontWeight: 600,
          }}
        >
          Insert
        </Button>
      }
    >
      {props.children}
    </Tooltip>
  );
};

// export const Tabs = withStyles({
//   scrollButtons: {
//     color: 'black',
//     height: '35px',
//   },
//   root: {
//     minHeight: '40px',
//     alignItems: 'center',
//   },
//   indicator: {},
// })(MuiTabs);

// export const Tab = withStyles({
//   wrapper: {
//     fontWeight: 'bold',
//   },
//   root: {
//     minWidth: '90px',
//     minHeight: '30px',
//     padding: '4px 8px',
//   },
// })(MuiTab);

export const getQuoteWrappedContent = (i: number, contentLength: number, html: string) => {
  if (contentLength === 1) {
    return (
      <>
        <OpeningQuoteIcon style={{ marginRight: '1px' }} />
        {html}
        <ClosingQuoteIcon style={{ marginLeft: '1px' }} />
      </>
    );
  }
  if (i === 0) {
    return (
      <>
        <OpeningQuoteIcon style={{ marginRight: '1px' }} />
        {html}
      </>
    );
  }
  if (i === contentLength - 1) {
    return (
      <>
        {html}
        <ClosingQuoteIcon style={{ marginLeft: '1px' }} />
      </>
    );
  }
  return html;
};
